import {
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Portal,
  Stack,
  Wrap,
  WrapItem,
  Divider,
  Text,
} from "@chakra-ui/react";
import { Text14 } from "components/Text";

interface TwoTierPopoverTagColumnOptions<TItem> {
  primaryItems: TItem[];
  secondaryItems: TItem[];
  renderTag: (item: TItem) => React.ReactNode;
  displaySummary?: (items: TItem[], summary: string) => React.ReactNode;
  generateSummary?(items: TItem[]): string;
  keyFn?: (item: TItem) => string;
  emptyLabel?: string;
  secondaryLabel?: string;
}

export default function TwoTierPopoverTagColumn<TItem>({
  primaryItems = [],
  secondaryItems = [],
  renderTag,
  displaySummary = (_, summary) => (<Text14>{summary}</Text14>),
  generateSummary = (innerItems: TItem[]) => `${innerItems.length} Items`,
  keyFn = (item: any) => item.id,
  emptyLabel = "None",
  secondaryLabel = "Other Items",
}: TwoTierPopoverTagColumnOptions<TItem>) {
  const hasItems = primaryItems.length > 0 || secondaryItems.length > 0;

  if (!hasItems) {
    return <Text14 bg="bg.muted" py="1" px="2" borderRadius="md">{emptyLabel}</Text14>;
  }

  return (
    <Popover trigger="hover" isLazy>
      <PopoverTrigger>
        <Box>
          {displaySummary(primaryItems, generateSummary(primaryItems))}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody>
            <Stack gap="2">
              {primaryItems.length > 0 && (
                <Wrap>
                  {primaryItems.map((item) => (
                    <WrapItem key={keyFn(item)}>{renderTag(item)}</WrapItem>
                  ))}
                </Wrap>
              )}

              {secondaryItems.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="sm" color="helperText.500" mb="2">
                      {secondaryLabel}
                    </Text>
                    <Wrap>
                      {secondaryItems.map((item) => (
                        <WrapItem key={keyFn(item)}>{renderTag(item)}</WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                </>
              )}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
